// ============================================================================
// Pre-submission record validation for the A2A legacy e-file flow.
//
// The IRS schema-validates the WHOLE transmission, so a single record with an
// invalid US state / ZIP rejects every record in the chunk. Rather than fake the
// data (a Canadian recipient under a made-up US state would be an incorrect return),
// we QUARANTINE records that can't be filed cleanly: file the valid ones, skip the
// invalid ones, and report which/why. Quarantined records keep eFiled=false, so once
// their data is corrected they file on the next run automatically.
// ============================================================================

// Valid US state / territory / military codes (IRS StateAbbreviationCdType enumeration).
export const US_STATE_CODES = new Set<string>([
  "AL", "AK", "AS", "AZ", "AR", "CA", "CO", "MP", "CT", "DE", "DC", "FM",
  "FL", "GA", "GU", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME",
  "MH", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PW", "PA", "PR", "RI", "SC",
  "SD", "TN", "TX", "VI", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
  "AA", "AE", "AP",
]);

// IRS ZIPCodeType: 5 digits, optionally + 4 or + 7 more.
export const ZIP_RE = /^[0-9]{5}([0-9]{4}|[0-9]{7})?$/;

export interface QuarantinedRecord {
  id: number | null;
  reasons: string[];
}

/** Return the reasons a record can't be filed cleanly (empty array = fileable). */
export function validateFileableRecord(record: any): string[] {
  const reasons: string[] = [];

  // FOREIGN recipients are decided by FOREIGN_COUNTRY (or the foreign indicator) — NOT by
  // the state field, which for a foreign record usually holds a foreign province (NB, QC,
  // etc.). No foreign address is supported yet (needs the IRS ForeignAddressGrp), so flag
  // them uniformly and skip the US state/ZIP checks — the whole address needs foreign handling.
  const foreignCountry = String(record?.FOREIGN_COUNTRY ?? "").trim();
  const foreignInd = String(record?.FOREIGN_COUNTRY_INDICATOR ?? "").trim();
  if (foreignCountry !== "" || (foreignInd !== "" && foreignInd !== "0")) {
    reasons.push(
      `foreign address (country '${foreignCountry || "unspecified"}') not yet supported`
    );
    return reasons;
  }

  // US recipient — state required, and must be a valid US/territory/military code.
  // A missing state makes the generator emit the invalid placeholder "XX".
  const stateRaw = record?.RECIPIENT_STATE;
  const state = String(stateRaw ?? "").trim().toUpperCase();
  if (!state) {
    reasons.push("missing recipient state");
  } else if (!US_STATE_CODES.has(state)) {
    reasons.push(`invalid recipient state '${stateRaw}'`);
  }

  // Recipient ZIP — if present, must match the IRS ZIP pattern (digits only).
  const zipRaw = record?.RECIPIENT_ZIP;
  const zip = String(zipRaw ?? "").trim();
  if (zip && !ZIP_RE.test(zip.replace(/[^0-9]/g, ""))) {
    reasons.push(`invalid recipient ZIP '${zipRaw}'`);
  }

  return reasons;
}

/** Split records into the ones we can file and the ones to quarantine (with reasons). */
export function partitionFileableRecords(records: any[]): {
  valid: any[];
  quarantined: QuarantinedRecord[];
} {
  const valid: any[] = [];
  const quarantined: QuarantinedRecord[] = [];
  for (const r of records) {
    const reasons = validateFileableRecord(r);
    if (reasons.length) {
      quarantined.push({ id: r?.id != null ? Number(r.id) : null, reasons });
    } else {
      valid.push(r);
    }
  }
  return { valid, quarantined };
}

/** A short, human-readable summary of the records that failed validation (for UI / logs). */
export function summarizeFailures(
  invalid: QuarantinedRecord[],
  total: number,
  maxItems = 5
): string {
  if (!invalid.length) return "";
  const head = invalid
    .slice(0, maxItems)
    .map((q) => `id ${q.id ?? "?"}: ${q.reasons.join(", ")}`)
    .join("; ");
  const more = invalid.length > maxItems ? ` (+${invalid.length - maxItems} more)` : "";
  return `${invalid.length}/${total} record(s) invalid: ${head}${more}`;
}
